import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App as AntApp } from "antd";
import t from "../../../app/i18n";
import { userApi } from "../../../modules/user/api";
import {
  AdminCreateUpdateAgentUserAccount,
  AdminCreateUserWithAgentUserAccounts,
  AdminUpdateUser,
  ConfirmUser,
  CreateUserTotpDevice,
  CreateUserTotpDeviceSecret,
  DeleteUserTotpDevice,
  PasswordReset,
  RequestPasswordReset,
  RequestUserEmailUpdate,
  RequestUserPhoneUpdate,
  UpdateUserEmail,
  UpdateUserPhone,
  UpdateUserTotpDevice,
  UserSendTotpCodeRequest,
  UserUpdatePassword,
} from "../../../modules/user/types";
import type { UUID } from "../../../typings/global";
import {
  EntityIdObject,
  EntityObject,
  SearchPageRequest,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../types";

export const USER_QUERY_KEYS = {
  adminFilterUsers: (filter?: SearchPageRequest) => (filter ? ["adminFilterUsers", filter] : ["adminFilterUsers"]),
  adminGetUser: (id?: UUID) => (id ? ["adminGetUser", id] : ["adminGetUser"]),
  adminGetUsersByAgent: (agentId?: UUID) => (agentId ? ["adminGetUsersByAgent", agentId] : ["adminGetUsersByAgent"]),
  adminUpdateUser: () => ["adminUpdateUser"],
  adminResendUserConfirmLink: () => ["adminResendUserConfirmLink"],
  adminAddAgentUserAccount: () => ["adminAddAgentUserAccount"],
  adminUpdateAgentUserAccount: () => ["adminUpdateAgentUserAccount"],
  adminCreateUserWithAgentUserAccounts: () => ["adminCreateUserWithAgentUserAccounts"],
  adminDeleteAgentUserAccount: () => ["adminDeleteAgentUserAccount"],
  agentGetUserInvitationsByAgent: (agentId?: UUID) =>
    agentId ? ["agentGetUserInvitationsByAgent", agentId] : ["agentGetUserInvitationsByAgent"],
  agentResendUserInvitation: () => ["agentResendUserInvitation"],
  userConfirmUser: () => ["userConfirmUser"],
  userUpdateEmail: () => ["userUpdateEmail"],
  resetPassword: () => ["resetPassword"],
  requestPasswordReset: () => ["requestPasswordReset"],
  userSendTotpCodeViaSms: () => ["userSendTotpCodeViaSms"],
  userGetUserTotpDevices: (userId?: UUID) => (userId ? ["userGetUserTotpDevices", userId] : ["userGetUserTotpDevices"]),
  userCreateUserTotpDeviceSecret: () => ["userCreateUserTotpDeviceSecret"],
  userCreateUserTotpDevice: () => ["userCreateUserTotpDevice"],
  userUpdateUserTotpDevice: () => ["userUpdateUserTotpDevice"],
  userDeleteUserTotpDevice: () => ["userDeleteUserTotpDevice"],
  userGetUserProfile: (userId?: UUID) => (userId ? ["userGetUserProfile", userId] : ["userGetUserProfile"]),
  userUpdatePassword: () => ["userUpdatePassword"],
  userRequestEmailUpdate: () => ["userRequestEmailUpdate"],
  userRequestPhoneUpdate: () => ["userRequestPhoneUpdate"],
  userUpdatePhone: () => ["userUpdatePhone"],
};

export const useAdminFilterUsers = (filter: SearchPageRequest) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.adminFilterUsers(filter),
    queryFn: async () => {
      const { data } = await userApi.adminFilterUsers(filter);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useAdminGetUser = (id: UUID) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.adminGetUser(id),
    queryFn: async () => {
      const { data } = await userApi.adminGetUser({ id });

      return data;
    },
  });
};

export const useAdminGetUsersByAgent = (agentId: UUID) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.adminGetUsersByAgent(agentId),
    queryFn: async () => {
      const { data } = await userApi.adminGetUsersByAgent({ agentId });

      return data;
    },
  });
};

export const useAdminUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminUpdateUser(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<AdminUpdateUser>) => {
      const { data } = await userApi.adminUpdateUser(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUser() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminFilterUsers() });

      Notification.itemUpdatedNotification();
    },
  });
};

export const useAdminResendUserConfirmLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminResendUserConfirmLink(),
    mutationFn: async (payload: EntityIdObject) => {
      const { data } = await userApi.adminResendUserConfirmLink(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUser() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.resendConfirmationLinkSuccess"),
      });
    },
  });
};

export const useAdminAddAgentUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminAddAgentUserAccount(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<AdminCreateUpdateAgentUserAccount>) => {
      const { data } = await userApi.adminAddAgentUserAccount(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUser() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });

      Notification.itemCreatedNotification();
    },
  });
};

export const useAdminUpdateAgentUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminUpdateAgentUserAccount(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<AdminCreateUpdateAgentUserAccount>) => {
      const { data } = await userApi.adminUpdateAgentUserAccount(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUser() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });

      Notification.itemUpdatedNotification();
    },
  });
};

export const useAdminCreateUserWithAgentUserAccounts = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminCreateUserWithAgentUserAccounts(),
    meta: { globalSpinner },
    mutationFn: async (payload: AdminCreateUserWithAgentUserAccounts) => {
      const { data } = await userApi.adminCreateUserWithAgentUserAccounts(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminFilterUsers() });

      Notification.itemCreatedNotification();
    },
  });
};

export const useAdminDeleteAgentUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.adminDeleteAgentUserAccount(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return userApi.adminDeleteAgentUserAccount(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUser() });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.adminGetUsersByAgent() });

      Notification.itemDeletedNotification();
    },
  });
};

export const useAgentGetUserInvitationsByAgent = (agentId: UUID) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.agentGetUserInvitationsByAgent(agentId),
    queryFn: async () => {
      const { data } = await userApi.agentGetUserInvitationsByAgent({ agentId });

      return data;
    },
  });
};

export const useAgentResendUserInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.agentResendUserInvitation(),
    mutationFn: async (payload: EntityIdObject) => {
      const { data } = await userApi.agentResendUserInvitation(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.agentGetUserInvitationsByAgent() });

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.resendConfirmationLinkSuccess"),
      });
    },
  });
};

export const useUserConfirmUser = () => {
  return useMutation({
    mutationKey: USER_QUERY_KEYS.userConfirmUser(),
    mutationFn: async (payload: EntityObject<ConfirmUser>) => {
      const { data } = await userApi.userConfirmUser(payload);

      return data;
    },
  });
};

export const useUserUpdateEmail = () => {
  return useMutation({
    mutationKey: USER_QUERY_KEYS.userUpdateEmail(),
    mutationFn: async (payload: EntityObject<UpdateUserEmail>) => {
      const { data } = await userApi.userUpdateEmail(payload);

      return data;
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationKey: USER_QUERY_KEYS.resetPassword(),
    mutationFn: async (payload: EntityObject<PasswordReset>) => {
      const { data } = await userApi.resetPassword(payload);

      return data;
    },
  });
};

export const useRequestPasswordReset = () => {
  const { modal } = AntApp.useApp();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.requestPasswordReset(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: RequestPasswordReset) => {
      const { data } = await userApi.requestPasswordReset(payload);

      return data;
    },
    onSuccess: () => {
      modal.info({
        title: t("common.operationSuccess"),
        content: t("user.helpers.requestPasswordResetSuccess"),
        okText: t("user.actions.closeWindow"),
      });
    },
  });
};

export const useUserSendTotpCodeViaSms = () => {
  return useMutation({
    mutationKey: USER_QUERY_KEYS.userSendTotpCodeViaSms(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UserSendTotpCodeRequest>) => {
      const { data } = await userApi.userSendTotpCodeViaSms(payload);

      return data;
    },
    onSuccess: () => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.sendTotpCodeViaSmsSuccess"),
      });
    },
  });
};

export const useUserGetUserProfile = (userId?: UUID) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.userGetUserProfile(userId),
    queryFn: async () => {
      const { data } = await userApi.userGetUserProfile({ id: userId as UUID });

      return data;
    },
    enabled: !!userId,
  });
};

export const useUserUpdatePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userUpdatePassword(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UserUpdatePassword>) => {
      const { data } = await userApi.userUpdatePassword(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserProfile() });

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.passwordUpdateSuccess"),
      });
    },
  });
};

export const useUserRequestEmailUpdate = () => {
  const queryClient = useQueryClient();
  const { modal } = AntApp.useApp();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userRequestEmailUpdate(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<RequestUserEmailUpdate>) => {
      const { data } = await userApi.userRequestEmailUpdate(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserProfile() });

      modal.success({
        title: t("common.operationSuccess"),
        content: t("user.helpers.emailUpdateRequestSuccess"),
        okText: t("user.actions.closeWindow"),
      });
    },
  });
};

export const useUserRequestPhoneUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userRequestPhoneUpdate(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<RequestUserPhoneUpdate>) => {
      const { data } = await userApi.userRequestPhoneUpdate(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserProfile() });

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.sendTotpCodeViaSmsSuccess"),
      });
    },
  });
};

export const useUserUpdatePhone = () => {
  const queryClient = useQueryClient();
  const { modal } = AntApp.useApp();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userUpdatePhone(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateUserPhone>) => {
      const { data } = await userApi.userUpdatePhone(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserProfile() });

      modal.success({
        title: t("common.operationSuccess"),
        content: t("user.helpers.phoneUpdateSuccess"),
        okText: t("user.actions.closeWindow"),
      });
    },
  });
};

export const useUserGetUserTotpDevices = (userId?: UUID) => {
  return useQuery({
    queryKey: USER_QUERY_KEYS.userGetUserTotpDevices(userId),
    queryFn: async () => {
      const { data } = await userApi.userGetUserTotpDevices({ id: userId as UUID });

      return data;
    },
    enabled: !!userId,
  });
};

export const useUserCreateUserTotpDeviceSecret = () => {
  return useMutation({
    mutationKey: USER_QUERY_KEYS.userCreateUserTotpDeviceSecret(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUserTotpDeviceSecret>) => {
      const { data } = await userApi.userCreateUserTotpDeviceSecret(payload);

      return data;
    },
  });
};

export const useUserCreateUserTotpDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userCreateUserTotpDevice(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUserTotpDevice>) => {
      const { data } = await userApi.userCreateUserTotpDevice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserTotpDevices() });
    },
  });
};

export const useUserUpdateUserTotpDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userUpdateUserTotpDevice(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateUserTotpDevice>) => {
      const { data } = await userApi.userUpdateUserTotpDevice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserTotpDevices() });

      Notification.itemUpdatedNotification();
    },
  });
};

export const useUserDeleteUserTotpDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: USER_QUERY_KEYS.userDeleteUserTotpDevice(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<DeleteUserTotpDevice>) => {
      const { data } = await userApi.userDeleteUserTotpDevice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userGetUserTotpDevices() });

      Notification.itemDeletedNotification();
    },
  });
};
